import { useEffect, useState, useMemo, useCallback, useRef } from "react"
import * as XLSX from "xlsx"
import { useNavigate } from "react-router-dom"
import { CompaniesList } from "@/components/CompaniesList/CompaniesList"
import { Toolbar } from "@/organisms/Toolbar"
import { getCompaniesV2 } from "@/services/companyService"
import { useAuth } from "@/contexts/AuthContext"
import { ButtonGroup, Input, Select, SelectOption } from "@/components/molecules"
import { Button, Text } from "@/components/atoms"
import { CompaniesDash } from "@/components/CompaniesForm/CompaniesDash"
import { useSticky } from "@/hooks/useSticky"
import { ControlBar } from "@/components/organisms/ControlBar"
import { useModal } from "@/components/molecules/Modal/ModalProvider"
import MentoringBooking from "@/components/MentoringBooking"

const TIPO_PROGRAMA_OPTIONS = [
  { value: "investidor", label: "Giants Investidor" },
  { value: "socio", label: "Giants Sócio" },
  { value: "giants", label: "Giants" },
  { value: "pac", label: "PAC" },
]

const Companies = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { showModal, hideModal } = useModal()
  const [sentinelRef] = useSticky<HTMLDivElement>({ rootMargin: "-60px" })

  // Ref para detectar o final da lista (Infinite Scroll)
  const loadMoreRef = useRef<HTMLDivElement>(null)

  const breadcrumbItems = [
    { href: "/", label: "Início" },
    { href: "/empresas", label: "Empresas", isCurrent: true },
  ]

  // --- States de Dados ---
  const [companiesData, setCompaniesData] = useState<any[]>([])
  const [dashData, setDashData] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [exportingCarteira, setExportingCarteira] = useState(false)

  // --- States de Controle ---
  const [searchTerm, setSearchTerm] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [currentPage, setCurrentPage] = useState(1)

  const [sortOrder, setSortOrder] = useState("")
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc")
  const [isFilterOpened, setIsFilterOpened] = useState(true)
  const [selectedControlTab, setSelectedControlTab] = useState("filters")
  const [itemsPerPage, setItemsPerPage] = useState("10")

  const [filters, setFilters] = useState({
    tiposPrograma: [] as string[],
    status: "all",
    apenasPrincipal: false, // Este controla o apenasPrincipal
    apenasEspecialista: false,
  })

  // --- Lógica de Debounce ---
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm)
    }, 500)
    return () => clearTimeout(handler)
  }, [searchTerm])

  // --- Data Fetching ---
  const fetchCompanies = useCallback(
    async (page: number, search: string, isNewSearch: boolean) => {
      setLoading(true)
      setError(null)

      try {
        // Construção dinâmica dos parâmetros de busca
        const params: any = { page, limit: itemsPerPage }

        if (search.trim()) params.search = search.trim()
        if (filters.tiposPrograma.length)
          params.tipoPrograma = filters.tiposPrograma.join(",")
        if (filters.apenasEspecialista) params.apenasEspecialista = true
        if (filters.apenasPrincipal) params.apenasPrincipal = true // Mapeado conforme solicitado
        if (filters.status !== "all") params.status = filters.status

        const response = await getCompaniesV2(params)

        if (isNewSearch) {
          setCompaniesData(response.data || [])
        } else {
          setCompaniesData((prev) => [...prev, ...(response.data || [])])
        }
        setDashData(response.meta)
      } catch (err) {
        setError("Erro ao carregar empresas")
        console.error(err)
      } finally {
        setLoading(false)
      }
    },
    [filters, itemsPerPage]
  )

  // Resetar e buscar quando o termo ou filtros mudarem
  useEffect(() => {
    setCurrentPage(1)
    fetchCompanies(1, debouncedSearch, true)
  }, [debouncedSearch, filters, fetchCompanies])

  // --- Lógica do Infinite Scroll ---
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && dashData?.hasMore && !loading) {
          const nextPage = currentPage + 1
          setCurrentPage(nextPage)
          fetchCompanies(nextPage, debouncedSearch, false)
        }
      },
      { threshold: 0.5 }
    )

    if (loadMoreRef.current) observer.observe(loadMoreRef.current)
    return () => observer.disconnect()
  }, [dashData?.hasMore, loading, currentPage, debouncedSearch, fetchCompanies])

  // --- Dash Statistics ---
  const mentorStats = useMemo(() => {
    if (dashData?.mentorQuantidades) {
      return {
        qtdMentor: dashData.mentorQuantidades.qtdMentor,
        qtdEspecialista: dashData.mentorQuantidades.qtdEspecialista,
      }
    }
    return { qtdMentor: 0, qtdEspecialista: 0 }
  }, [dashData])

  // --- Handlers ---
  const handleBookMentoring = (company: any) => {
    showModal({
      title: "Agendar Mentoria",
      size: "xl",
      content: (
        <MentoringBooking
          company={company}
          onSuccess={() => {
            hideModal()
            navigate("/mentorings")
          }}
        />
      ),
    })
  }

  // --- Exportação da Carteira (empresas como mentor principal ou especialista) ---
  // Busca todas as páginas de um papel (principal/especialista) sem depender do
  // filtro/paginação atual da tela, garantindo que a carteira exportada é completa.
  const fetchAllCompaniesByRole = async (roleParams: Record<string, boolean>) => {
    const pageSize = 1000
    const first = (await getCompaniesV2({ ...roleParams, limit: pageSize, page: 1 } as any)) as any
    const total: number = first?.meta?.total ?? first?.data?.length ?? 0
    if (total > (first?.data?.length ?? 0)) {
      const full = (await getCompaniesV2({ ...roleParams, limit: total, page: 1 } as any)) as any
      return full?.data || []
    }
    return first?.data || []
  }

  const handleExportCarteira = async () => {
    setExportingCarteira(true)
    setError(null)

    try {
      const [principais, especialista] = await Promise.all([
        fetchAllCompaniesByRole({ apenasPrincipal: true }),
        fetchAllCompaniesByRole({ apenasEspecialista: true }),
      ])

      const carteira = new Map<number, any>()

      for (const company of principais) {
        carteira.set(company.empId, { ...company, papel: "Principal" })
      }

      for (const company of especialista) {
        const existing = carteira.get(company.empId)
        if (existing) {
          existing.papel = "Principal e Especialista"
        } else {
          carteira.set(company.empId, { ...company, papel: "Especialista" })
        }
      }

      const rows = Array.from(carteira.values())
        .sort((a, b) => (a.nomeFantasia || "").localeCompare(b.nomeFantasia || ""))
        .map((company) => ({
          Empresa: company.nomeFantasia || "",
          "Razão Social": company.razaoSocial || "",
          CNPJ: company.cnpj || "",
          Papel: company.papel,
          "Mentor Principal da Empresa": company.nome || "",
          "Tipo de Programa": company.tipoProgramaDesc || "",
          Telefone: company.telefoneEmpresa || "",
          Email: company.emailEmpresa || "",
          "Mentorias da Empresa (Total)": company.qtdMentoriasTotal ?? 0,
          "Mentorias da Empresa (Finalizadas)": company.qtdMentoriasFinalizadas ?? 0,
          "Mentorias da Empresa (Pendentes)": company.qtdMentoriasPendentes ?? 0,
          "Mentorias da Empresa (Disponíveis)": company.qtdMentoriasDisponiveis ?? 0,
        }))

      if (rows.length === 0) {
        setError("Nenhuma empresa vinculada encontrada para exportar")
        return
      }

      const ws = XLSX.utils.json_to_sheet(rows)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, "Carteira")

      const dataISO = new Date().toISOString().slice(0, 10)

      if (user?.admin) {
        XLSX.writeFile(wb, `todas-as-empresas-${dataISO}.xlsx`)
      } else {
        const mentorSlug = (user?.nome || "mentor")
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .replace(/[^a-zA-Z0-9]+/g, "-")
          .toLowerCase()
        XLSX.writeFile(wb, `carteira-${mentorSlug}-${dataISO}.xlsx`)
      }
    } catch (err) {
      setError("Erro ao exportar carteira")
      console.error(err)
    } finally {
      setExportingCarteira(false)
    }
  }

  const clearFilters = () => {
    setFilters({
      tiposPrograma: [],
      status: "all",
      apenasPrincipal: false,
      apenasEspecialista: false,
    })
    setSearchTerm("")
    setIsFilterOpened(false)
  }

  const appliedFiltersCount = [
    filters.tiposPrograma.length > 0,
    filters.apenasPrincipal,
    filters.apenasEspecialista,
    filters.status !== "all",
  ].filter(Boolean).length

  const itemsPerPageOptions: SelectOption[] = [
    { label: "10", value: "10" },
    { label: "50", value: "50" },
    { label: "100", value: "100" },
    { label: "200", value: "200" },
  ]

  return (
    <section className="flex flex-col gap-4 duration-700 animate-in fade-in">
      <Toolbar breadcrumbItems={breadcrumbItems} />

      <section className="px-8 pb-10 pt-20">
        <CompaniesDash
          onMainMentorCounterCLick={() =>
            setFilters((f) => ({
              ...f,
              apenasPrincipal: !f.apenasPrincipal,
              apenasEspecialista: false,
            }))
          }
          onSpecialistMentorCounterClick={() =>
            setFilters((f) => ({
              ...f,
              apenasEspecialista: !f.apenasEspecialista,
              apenasPrincipal: false,
            }))
          }
          showOnlyapenasPrincipal={filters.apenasPrincipal}
          showOnlyMySpecialistCompanies={filters.apenasEspecialista}
          mentorData={mentorStats}
        />
      </section>

      <div ref={sentinelRef} />

      <div className="sticky top-14 z-20 flex flex-col justify-between gap-4 bg-dark p-10">
        <ControlBar
          isControlBarOpened={isFilterOpened}
          currentTab={selectedControlTab}
          onTabChange={(id) => {
            setSelectedControlTab(id)
            setIsFilterOpened(id === selectedControlTab ? !isFilterOpened : true)
          }}
          showClearFilters={appliedFiltersCount > 0}
          onClearFilters={clearFilters}
          tabs={[
            { label: "Filtros", id: "filters", counter: appliedFiltersCount, iconName: "filters" },
          ]}
        >
          
          <div className="flex flex-col gap-2">
            <Text variant="label">Tipo de Programa</Text>
            <ButtonGroup
              items={TIPO_PROGRAMA_OPTIONS}
              value={filters.tiposPrograma}
              multiple
              onSelectionChange={(v) =>
                setFilters((f) => ({ ...f, tiposPrograma: v as string[] }))
              }
            />
          </div>
        </ControlBar>

        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-2">
            <div className="w-96">
              <Input
                iconLeft="search"
                placeholder="Pesquisar..."
                variant="outline"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2">
              <Select
                id="itemsPerPage"
                variant="filled"
                onValueChange={(e) => setItemsPerPage(e)}
                placeholder="10 Itens por página"
                value={itemsPerPage}
                options={itemsPerPageOptions}
              />
              <Text className="text-nowrap">Itens por página</Text>
              <Button
                size="sm"
                variant="ghost"
                disabled={
                  exportingCarteira || mentorStats.qtdMentor + mentorStats.qtdEspecialista === 0
                }
                onClick={handleExportCarteira}
              >
                {exportingCarteira
                  ? "Exportando..."
                  : user?.admin
                    ? "Exportar Todas as Empresas"
                    : "Exportar Carteira"}
              </Button>
            </div>
          </div>
          <Text
            className="text-dark-300"
            variant="body"
          >
            {companiesData.length} resultados carregados {dashData && `de ${dashData.total}`}
          </Text>
        </div>
      </div>

      <section
        className="grid gap-4 px-8 py-4"
        style={{ gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))" }}
      >
        {companiesData.map((company) => (
          <CompaniesList
            key={`${company.empId}-${company.cnpj}`}
            company={company}
            onCardClick={() => navigate(`/companies/${company.empId}`)}
            onActionClick={() => handleBookMentoring(company)}
          />
        ))}
      </section>

      {/* Área de Infinite Scroll e Mensagens de Status */}
      <div
        ref={loadMoreRef}
        className="flex min-h-[100px] flex-col items-center justify-center py-10"
      >
        {loading && (
          <div className="flex items-center gap-3">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            <Text className="text-primary">Carregando mais...</Text>
          </div>
        )}

        {!dashData?.hasMore && companiesData.length > 0 && (
          <div className="flex w-full items-center gap-4 px-8">
            <div className="h-[1px] flex-1 bg-dark-400" />
            <Text className="text-dark-400">Fim da listagem</Text>
            <div className="h-[1px] flex-1 bg-dark-400" />
          </div>
        )}

        {error && <Text className="text-red-500">{error}</Text>}
      </div>
    </section>
  )
}

export default Companies
